import React, { useState } from "react";
import { updateRole } from "@api/rolesController";
import {
  Button,
  Flow,
  FlowActions,
  FlowHeader,
  FlowTitle,
  FlowBody,
} from "@components";
import { toast } from "@components/toast/Toast";
import { useUser } from "@auth/UserContext";
import RoleForm from "../forms/RoleForm";
import { useForm } from "react-hook-form";

const EditRole = ({ editRoleDetails, onComplete, onClose }) => {
  const [loading, setLoading] = useState(false);
  const { reload: reloadUser } = useUser();

  const {
    id,
    name,
    paymentRequestLevel,
    isAdmin,
    canUpdateSettings,
    customerLevel,
    paymentLevel,
    messageTemplateLevel,
    paymentRequestTemplateLevel,
    payoutLevel,
    reconciliationLevel,
    ledgerPaymentLevel,
    useAiFeatures,
  } = editRoleDetails;

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isValid },
    clearErrors,
  } = useForm({
    defaultValues: {
      name: name ?? "",
      paymentRequestLevel: paymentRequestLevel ?? "user",
      isAdmin: isAdmin ?? false,
      canUpdateSettings: canUpdateSettings ?? false,
      customerLevel:
        customerLevel === "requestfrom" ? "full" : (customerLevel ?? "none"),
      paymentLevel:
        paymentLevel === "refund" ? "full" : (paymentLevel ?? "none"),
      messageTemplateLevel:
        messageTemplateLevel === "update" ? "full" : (messageTemplateLevel ?? "none"),
      paymentRequestTemplateLevel:
        paymentRequestTemplateLevel === "update"
          ? "full"
          : (paymentRequestTemplateLevel ?? "view"),
      payoutLevel: payoutLevel ?? "none",
      reconciliationLevel:
        reconciliationLevel === "action"
          ? "full"
          : (reconciliationLevel ?? "none"),
      ledgerPaymentLevel:
        ledgerPaymentLevel === "action"
          ? "full"
          : (ledgerPaymentLevel ?? "none"),
      useAiFeatures: useAiFeatures ?? false,
    },
  });

  const selectedPaymentRequestLevel = watch("paymentRequestLevel");

  const selectedAccessPermissions = {
    customerLevel: watch("customerLevel"),
    paymentLevel: watch("paymentLevel"),
    messageTemplateLevel: watch("messageTemplateLevel"),
    paymentRequestTemplateLevel: watch("paymentRequestTemplateLevel"),
    payoutLevel: watch("payoutLevel"),
    reconciliationLevel: watch("reconciliationLevel"),
    ledgerPaymentLevel: watch("ledgerPaymentLevel"),
  };

  const onSubmit = async (values) => {
    setLoading(true);
    await saveRoleEdits(values);
    // Refresh the current user's memberships so any permission change to their own role
    // (e.g. AI features) takes effect immediately without a page refresh.
    await reloadUser();
    setLoading(false);

    toast({ description: `Updated "${values.name}" role` });
    onComplete();
    handleClose();
  };

  const saveRoleEdits = async (values) => {
    const correctedValues = {
      ...values,
      customerLevel:
        values.customerLevel === "full" ? "requestfrom" : values.customerLevel,
      paymentLevel:
        values.paymentLevel === "full" ? "refund" : values.paymentLevel,
      messageTemplateLevel:
        values.messageTemplateLevel === "full" ? "update" : values.messageTemplateLevel,
      paymentRequestTemplateLevel:
        values.paymentRequestTemplateLevel === "full" ? "update" : values.paymentRequestTemplateLevel,
      reconciliationLevel:
        values.reconciliationLevel === "full"
          ? "action"
          : values.reconciliationLevel,
      ledgerPaymentLevel:
        values.ledgerPaymentLevel === "full"
          ? "action"
          : values.ledgerPaymentLevel,
    };

    await updateRole({ id: id, ...correctedValues });
  };

  const handleClose = () => {
    reset();
    clearErrors();
    onClose();
  };

  return (
    <Flow isOpen={!!editRoleDetails} onClose={handleClose}>
      <FlowHeader onClose={handleClose}>
        <FlowTitle>Edit role</FlowTitle>
        <FlowActions>
          <Button
            color="secondary"
            type="button"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            onClick={handleSubmit(onSubmit)}
            submitting={loading}
            disabled={!isValid}
          >
            Save changes
          </Button>
        </FlowActions>
      </FlowHeader>
      <FlowBody>
        <div className="flex flex-col items-center w-full h-full">
          <RoleForm
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            setValue={setValue}
            control={control}
            errors={errors}
            selectedPaymentRequestLevel={selectedPaymentRequestLevel}
            selectedAccessPermissions={selectedAccessPermissions}
          />
        </div>
      </FlowBody>
    </Flow>
  );
};

export default EditRole;
